import { UIIcon } from '../config/icons';
import { ABCitySelect, ABLanguageSelect, ABPlanSelect, ABProducerSelect } from '../lib/ab/select-admin';
import { CAction } from '../lib/mdl/c-action';
import { cBookingPartChangeQuantity, cBookingPartConfirm, cBookingPartReject } from '../lib/mdl/c-booking';
import { cBookingAddFreeTickets, CBookingAdminView, cBookingCreate, cBookingForceUnbook, cBookingNoShow, cBookingPartPrioritize, cBookingPartStandbyReserve, cBookingsAdminViewByUser } from '../lib/mdl/c-booking-admin';
import { cCountryDefaultCurrency } from '../lib/mdl/c-country';
import { cSessionsGetUpcoming, CSessionUpcomingView } from '../lib/mdl/c-event-admin';
import { CUserProducerRole } from '../lib/mdl/c-producer-admin';
import { c_status_name, CPlanId, CSubscription, cUserAccountStatus, cUserFullName } from '../lib/mdl/c-user';
import { cUserAction, CUserAdminView, cUserAdminViewGet, cUserCreate, cUserDelete, cUserGetInfo, cUserUpdate, cUserUpdatePassword, cUserUpdatePermissions, cUserUpdateProducerRoles } from '../lib/mdl/c-user-admin';
import { ePaymentMethodDescription } from '../lib/mdl/e-payment-method';
import { EHUserTransaction } from '../lib/mdl/e-user-transaction';
import { l_permission_specs, LPermission, LPermissionSpec } from '../lib/mdl/l-permission';
import { MDelivery } from '../lib/mdl/m-message';
import { canManagePermissions, canViewPermissions } from '../lib/me/can';
import { the_alt_token } from '../lib/me/login';
import { UIInputAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UIAutofill } from '../lib/ui/component/input-base';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIList } from '../lib/ui/component/list';
import { UIFlagsSelect, UISelect } from '../lib/ui/component/select';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UITickCheckbox } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { $fmt } from '../lib/ui/fmt';
import { UIScreen } from '../lib/ui/screen';
import { UIRelPos } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { $amount, YPaymentAmount } from '../lib/y/amount';
import { YArrayDataSource } from '../lib/y/data-source';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { $format } from '../lib/y/format';
import { yGetI18N } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { $date, $time, YDate } from '../lib/y/time';
import { YId } from '../lib/y/types';
import { AdminSendMessageDialog } from './templates';

class ApplyDiscountDialog extends UIDialog {
    constructor(user: CUserAdminView) {
        super({ close: true, classes: 'm-500' });

        const amount = new UINumberInput({ scale: 100, placeholder: 'Amount', required: true });
        const months = new UINumberInput({ placeholder: 'Months', required: true });
        const name = new UITextInput({ placeholder: 'Name', required: true })

        const form = $form({ _: [amount, months, name] });

        const currency = cCountryDefaultCurrency(user.country);

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Apply discount'),
            $el.P('info', 'Choose discount'),
            new UILabeled(amount, { before: currency }),
            months,
            name,
            $el.DIV('l-button-row', new UIButton({
                title: "Apply", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cUserAction(user.id, 'add_addon', { amount: { value: -amount.value!, currency }, duration: months.value!, name: name.value }).then(() => {
                        this.dismiss(true)
                    }, uiToast);
                }
            }))
        ]));
    }
}

class AddFreeSubscription extends UIDialog {
    constructor(user: CUserAdminView) {
        super({ close: true, classes: 'm-500' });

        const kind = new ABPlanSelect({ required: true });
        kind.value = 'plusone';
        const months = new UINumberInput({ placeholder: 'Months', required: true });

        const form = $form({ _: [kind, months] });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Add free subscription'),
            kind,
            months,
            $el.DIV('l-button-row', new UIButton({
                title: "Add", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cUserAction(user.id, 'add_free_subscription', { plan: kind.value!, months: months.value! }).then(() => {
                        this.dismiss(true);
                    }, uiToast);
                }
            }))
        ]));
    }
}

class ExtendPauseDialog extends UIDialog {
    constructor(user: CUserAdminView) {
        super({ close: true, classes: 'm-500' });

        const months = new UINumberInput({ placeholder: 'Months', required: true });

        const form = $form({ _: [months] });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Extend pause'),
            months,
            $el.DIV('l-button-row', new UIButton({
                title: "Extend", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cUserAction(user.id, 'force_pause', { months: months.value! }).then(() => {
                        this.dismiss(true);
                    }, uiToast);
                }
            }))
        ]));
    }
}

class AddBookingDialog extends UIDialog {
    constructor(user: CUserAdminView, all_sessions: CSessionUpcomingView[]) {
        super({ close: true, classes: 'm-500' });

        const by_event = Object.groupBy(all_sessions, s => s.event.id);

        const event_values = Object.entries(by_event).map(e => ({ key: e[0], name: yGetI18N(e[1]![0].event.name) }));
        event_values.sort((a, b) => a.name.localeCompare(b.name));

        const event = new UISelect<YId>(event_values, { placeholder: 'Select event', required: true });
        const session = new UISelect<YId>(undefined, { placeholder: 'Select session', required: true });

        $on(event, 'value', e => {
            if(!e) return;
            const session_values = by_event[e]!;
            session.values = session_values.map(s => ({ key: s.id, name: $time(s.time).format('yyyy-MM-dd HH:mm') }));
            session.value = session_values.length ? session_values[0].id : undefined;
        });

        const amount = new UISelect([{ key: 1, name: '1 ticket' }, { key: 2, name: '2 tickets' }], { required: true });
        amount.value = 1;

        const is_free = new UITickCheckbox({ title: 'Don\'t count for booking limit' });
        const allow_overbook = new UITickCheckbox({ title: 'Allow overbooking' });

        const form = $form({ event, session, amount, is_free, allow_overbook });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Create booking'),
            $el.P('info', 'For user: ' + cUserFullName(user)),
            event,
            session,
            amount,
            is_free,
            allow_overbook,
            $el.DIV('l-button-row', new UIButton({
                title: "Book", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cBookingCreate(user.id, event.value!, session.value!, amount.value!, is_free.value, allow_overbook.value).then(() => {
                        this.dismiss(true);
                    });
                }
            }))
        ]));
    }
}

class AddFreeTicketsDialog extends UIDialog {
    constructor(booking: CBookingAdminView) {
        super({ close: true, classes: 'm-500' });

        const quantity = new UINumberInput({ required: true });
        quantity.value = 1;

        const form = $form({ _: quantity });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Add free tickets'),
            $el.P('info', booking.event_name),
            quantity,
            $el.DIV('l-button-row', new UIButton({
                title: "Add", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cBookingAddFreeTickets(booking.id, quantity.value!).then(() => { this.dismiss(true) });
                }
            }))
        ]));
    }
}

class ChangePasswordDialog extends UIDialog {
    constructor(user: CUserAdminView) {
        super({ close: true, classes: 'm-500' });

        const password = new UITextInput({ type: 'password', placeholder: 'Password', autofill: UIAutofill.NEW_PASSWORD, required: true });
        const confirm = new UITextInput({
            type: 'password', placeholder: "Confirm password", autofill: UIAutofill.CONFIRM_PASSWORD, required: true,
            validator: (v) => (v === password.value)
        });
        const form = $form({ password, _: confirm });

        this.content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', 'Change password'),
            $el.P('info', 'For user: ' + cUserFullName(user)),
            password,
            confirm,
            $el.DIV('l-button-row', new UIButton({
                title: "Change password", classes: 'm-round m-compact', on_click: () => {
                    if(!form.validate()) return;
                    return cUserUpdatePassword(user, password.value).then(() => { this.dismiss(true) });
                }
            }))
        ]));
    }
}

class UserInfoTab extends UIView {
    constructor(user?: CUserAdminView) {
        super('div', 'tl-admin-page');

        const first_name = new UITextInput({ required: true, autofill: UIAutofill.FIRST_NAME });
        const last_name = new UITextInput({ required: true, autofill: UIAutofill.LAST_NAME });
        const email = new UITextInput({ type: 'email', required: true, autofill: UIAutofill.EMAIL });
        const copy_invite_code = $el.ICON(UIIcon.COPY, 'u-clickable');
        const invite_code = new UITextInput({ disabled: true, r_deco: copy_invite_code });
        const phone = new UITextInput({ type: 'phone', autofill: UIAutofill.PHONE, null_if_empty: true });
        const city_id = new ABCitySelect({ signup: true, required: true });
        const language = new ABLanguageSelect({ required: true })
        const no_recommendation_emails = new UITickCheckbox({ title: "Don't send recommendation emails" });

        const form = $form({ _: [no_recommendation_emails], city_id, first_name, last_name, email, phone, invite_code, language });

        const email_labeled = new UILabeled(email, { above: 'Email' });

        const section = $el.FORM('ui-section w-form l-form', [
            $el.DIV('l-input-row', [
                new UILabeled(first_name, { above: 'First name' }),
                new UILabeled(last_name, { above: 'Last name' })
            ]),
            email_labeled,
            new UILabeled(phone, { above: 'Phone number' }),
            new UILabeled(city_id, { above: 'City' }),
            new UILabeled(language, { above: 'Language' }),
            user && new UILabeled(invite_code, { above: 'Invite code' }),
            no_recommendation_emails
        ]);

        if(user) {
            form.value = user;
            no_recommendation_emails.value = !user.messages.recommendation_emails;
            copy_invite_code.addEventListener('click', () => {
                navigator.clipboard.writeText('https://abundolive.se/?code=' + user.invite_code).then(() => {
                    uiToast('Link copied to clipboard');
                });
            });

            section.ui_add([
                new UIButton({
                    kind: 'k-text', title: 'Change password', on_click: () => {
                        new ChangePasswordDialog(user).present();
                    }
                }),
                new UIButton({
                    kind: 'k-text', title: 'Send password reset link', on_click: () => {
                        return cUserAction(user.id, 'send_password_reset').then(() => {
                            uiToast('Link sent to ' + user.email);
                            return true;
                        }, uiToast);
                    }
                }),
            ]);

            section.ui_add([
                new UIButton({
                    kind: 'k-text', title: 'Remove user and all their data', on_click: () => {
                        return showWarning({
                            title: 'Are you sure?', texts: ['If the user is in test mode or has never had a subscription or booking, the user will be permanently removed from the database', 'Otherwise, it will remove the name and email address for the user, so that they can\'t be identified',
                                'It will not work if the user has an active subscription or future bookings']
                        }).then(() => {
                            return cUserDelete(user.id).then($location.pop, uiToast);
                        });
                    }
                }),
                new UIButton({
                    kind: 'k-text', title: 'Download user data in JSON format',
                    link: { href: `/docs/user/${user.id}/info?token=${the_alt_token}`, download: 'Abundo-Data.json', here: true }
                })
            ]);

            this.classList.add('m-sidebar');
            const sidebar = $el.DIV('tl-sidebar')
            const status = cUserAccountStatus(user);

            sidebar.ui_add([
                $el.DIV('tl-sidebar-heading', 'Account status'),
                $el.B(c_status_name[status] || 'Invalid')
            ]);

            const buttons = $el.DIV('r-buttons');

            if(status == 'paused') {
                buttons.ui_add($el.DIV('r-button', new UIButton({
                    kind: 'k-text', title: 'Extend pause', on_click: () => {
                        new ExtendPauseDialog(user).present();
                    }
                })));
            }

            if(status == 'active' || status == 'pastdue' || status == 'pending_pause') {
                buttons.ui_add($el.DIV('r-button', new UIButton({
                    kind: 'k-text', title: 'Force pause', on_click: () => {
                        return showWarning({ title: 'Are you sure?', text: 'This will immediately start the pause and cancel all booked tickets without any notification to the user' }).then(() => {
                            return cUserAction(user.id, 'force_pause', { months: 1 }).catch(uiToast);
                        });
                    }
                })));
            }

            if(status == 'active' || status == 'pastdue' || status == 'pending_cancel' || status == 'giftcard') {
                buttons.ui_add($el.DIV('r-button', new UIButton({
                    kind: 'k-text', title: 'Force cancel', on_click: () => {
                        return showWarning({ title: 'Are you sure?', text: 'This will immediately cancel the user\'s subscription and cancel all booked tickets without any notification to the user' }).then(() => {
                            return cUserAction(user.id, 'force_cancel').catch(uiToast);
                        });
                    }
                })));
            }

            buttons.ui_add($el.DIV('r-button', new UIButton({
                kind: 'k-text', title: 'Add free subscription', on_click: () => {
                    new AddFreeSubscription(user).present();
                }
            })));

            sidebar.ui_add(buttons);

            if(user.subscriptions.length) {
                sidebar.ui_add([
                    $el.DIV('tl-sidebar-heading', 'Subscription kind'),
                    $el.B(user.plan ?? 'None')
                ])

                if(user.plan == 'standard') {
                    sidebar.ui_add(new UIButton({
                        kind: 'k-text', title: 'Force upgrade', on_click: () => {
                            return showWarning({ title: 'Are you sure?', text: 'This will immediately change the plan without any notification to the user' }).then(() => {
                                return cUserAction(user.id, 'force_change_plan', { plan: 'plusone' }).catch(uiToast);
                            });
                        }
                    }));
                } else {
                    sidebar.ui_add(new UIButton({
                        kind: 'k-text', title: 'Force downgrade', on_click: () => {
                            return showWarning({ title: 'Are you sure?', text: 'This will immediately change the plan without any notification to the user' }).then(() => {
                                return cUserAction(user.id, 'force_change_plan', { plan: 'standard' }).catch(uiToast);
                            });
                        }
                    }));
                }
            }

            sidebar.ui_add($el.DIV('tl-sidebar-heading', 'Addons'));

            const addons = getAddonInfo(user);

            if(addons.length) {
                for(const a of addons) {
                    sidebar.ui_add($el.B(a.description));
                }
            } else {
                sidebar.ui_add($el.B('No addons'));
            }

            sidebar.ui_add(new UIButton({
                kind: 'k-text', title: 'Add discount', on_click: () => {
                    new ApplyDiscountDialog(user).present();
                }
            }));

            this.ui_add(sidebar);
        } else {
            const password = new UITextInput({ type: 'password', autofill: UIAutofill.NEW_PASSWORD, null_if_empty: true });
            const is_test = new UITickCheckbox({ title: 'Create a test mode user' });
            const create_free = new UITickCheckbox({ title: 'Add a free subscription ' });
            const plan = new ABPlanSelect({ required: true });
            plan.value = 'plusone';
            const months = new UINumberInput({ placeholder: 'Months', required: true });

            section.ui_insert(new UILabeled(password, { above: 'Password' }), email_labeled, true);
            section.ui_add([
                is_test,
                $el.DIV('l-input-row', [create_free, plan, months])
            ]);

            form.add({ free_subscription: $form.when(create_free, $form({ plan, months })), password, is_test });
        }

        const button = new UIButton({
            icon: UIIcon.THUMBS_UP, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                if(!form.validate()) return;
                const value = form.value;
                value.messages = { recommendation_emails: !no_recommendation_emails.value };

                $log('app', 'value', value);

                if(user)
                    return cUserUpdate(user, value).then($location.pop, uiToast);
                else
                    return cUserCreate(value).then($location.pop, uiToast);
            }
        });
        addTooltip(user ? 'Update' : 'Create', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, button]);
    }
}

class UserRolesTab extends UIView {
    constructor(user: CUserAdminView) {
        super('div', 'tl-admin-page');

        const role_values = [{ key: 'admin', name: 'Administrator' }, { key: 'events', name: 'Manage events' }, { key: 'bookings', name: 'See bookings' },
        { key: 'economy', name: 'Economy' }];

        const roles_adapter = new UIInputAdapter((_role: CUserProducerRole, entry) => {
            const container = $el.DIV('l-input-row');
            const producer_id = new ABProducerSelect({});
            const roles = new UIFlagsSelect(role_values);
            container.ui_add([
                producer_id,
                roles,
                new UIButton({
                    icon: UIIcon.TRASH_CAN, on_click: () => {
                        roles_adapter.remove(entry);
                    }
                })
            ]);

            return { view: container, input: $form({ producer_id, roles }) };
        })

        const roles = new UIList(roles_adapter, { classes: 'l-form' });

        roles_adapter.value = user.producer_roles;

        const section = $el.FORM('ui-section w-form l-form', [
            new UILabeled(roles, { above: 'Producer roles' }),
            $el.DIV('l-button-row', [
                new UIButton({
                    title: 'Update', classes: 'm-round', on_click: () =>
                        cUserUpdateProducerRoles(user, roles_adapter.value).catch(uiToast)
                })
            ])
        ]);

        const button = new UIButton({
            icon: UIIcon.PLUS, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                roles_adapter.push({ roles: { admin: true } } as unknown as CUserProducerRole, true);
            }
        });
        addTooltip('Add role', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, button]);
    }
}

function transactionDescription(entry: EHUserTransaction) {
    switch(entry.kind) {
        case 'subscribe':
            return 'Subscription ' + (entry.items[0]?.description ?? '');
        case 'recurring':
            return 'Membership ' + (entry.items[0]?.description ?? '');
        case 'purchase':
            return 'Purchase';
        case 'change_plan':
            return 'Upgrade';
    }
    return '-';
}

interface AddonInfo {
    sort_date: YDate;
    description: string;
    until_text: string;
    amount: YPaymentAmount;
}

function getAddonInfo(user: CUserAdminView): AddonInfo[] {
    const plan: CPlanId = user.plan || 'standard'
    const info: AddonInfo[] = [];
    const today = $date.today();

    for(const addon of user.addons || []) {
        let sort_date: YDate;
        let until_text = '';
        const description = addon.name;
        if(addon.usage_max == 1) {
            if(addon.usage_count > 0) continue;

            sort_date = today;
            until_text = 'one time';
        } else {
            const ends_on = $date(addon.ends_on);
            sort_date = $date(addon.starts_on ?? 0);

            if(ends_on) {
                if(!ends_on.after(today)) continue;

                until_text = ends_on.format('yyyy-MM-dd');
            } else {
                until_text = 'Unlimited';
            }
        }

        info.push({
            sort_date: sort_date,
            until_text: until_text,
            description: description,
            amount: (addon.amount[plan] && addon.amount[plan]!.value) ? addon.amount[plan]! : addon.amount['any']!
        });
    }

    info.sort((a, b) => $time.compare(b.sort_date, a.sort_date));

    return info;
}

function getActionMoreInfo(_user: CUserAdminView, a: CAction, entries: EHUserTransaction[]) {
    switch(a.code) {
        case 'payment.addon':
            //TODO: something
            break;
        // return a.addon_name! + ' (' + $format.currency({ value: a.payment_amount!, currency: cCountryDefaultCurrency(user.model.country)}) + ')';
        case 'payment.fail':
            //TODOURGENT
            // for(const e of entries) {
            //     if(e.id == a.billing_entry)
            //         return $format.currency(e.amount) + ' - ' + (e.payment_failure ? e.payment_failure.message : 'Error'); // TODO: use currency
            // }
            break;
        case 'payment.success':
            for(const e of entries) {
                if(e.id == a.user_transaction_id)
                    return $format.currency(e.amount);
            }
    }

    return '';
}

class UserSubscriptionTab extends UIView {
    constructor(user: CUserAdminView) {
        super('div', 'tl-admin-page');

        let transactions: EHUserTransaction[] = [];

        const subs_def = new UITableDefinition<CSubscription>();
        subs_def.addValue(s => $date(s.start_date), $fmt.DATE(), { title: 'Start', width: -120 });
        subs_def.addValue(s => $date(s.end_date), $fmt.DATE(), { title: 'End', width: -120 });
        subs_def.addValue(s => s.plan, { title: 'Plan', width: -100 });

        const subs_source = new YArrayDataSource(user.subscriptions.filter(s => s.start_date != s.end_date));

        const action_def = new UITableDefinition<CAction>();
        action_def.addValue(a => $time(a.time), $fmt.DATE(), { title: 'Date', width: -100 });
        action_def.addValue(a => a.code, { title: 'Action', width: -160 });
        action_def.addValue(a => getActionMoreInfo(user, a, transactions), { title: 'More info', width: 100 });

        const action_source = new YArrayDataSource<CAction>();

        const addon_def = new UITableDefinition<AddonInfo>();
        addon_def.addValue(a => a.until_text, { title: 'Until', width: -120, sort: false });
        addon_def.addValue(a => a.description, { title: 'Description', width: -200, sort: false });
        addon_def.addValue(a => a.amount, $fmt.CURRENCY, { title: 'Amount', width: -120, sort: false });

        const addon_source = new YArrayDataSource<AddonInfo>(getAddonInfo(user));

        const transaction_def = new UITableDefinition<EHUserTransaction>();
        transaction_def.addValue(e => $time(e.date), $fmt.DATE(), { title: "Date", width: -100 });
        transaction_def.addValue(e => transactionDescription(e), { title: "Description", width: 200 });
        transaction_def.addValue(e => e.payment_method ? ePaymentMethodDescription(e.payment_method) : '-', { title: "Payment method", width: 160 });
        transaction_def.addValue(e => e.is_refund ? $amount.neg(e.amount) : e.amount, $fmt.CURRENCY, { title: "Amount", width: -120 });

        transaction_def.addButtons([
            {
                icon: UIIcon.PAPERCLIP, tooltip: 'Kvitto', when: e => !e.is_refund && !e.is_failed,
                link: e => ({
                    href: `/docs/c/receipt/${e.id}?format=pdf&token=${the_alt_token}`,
                    download: `Abundo kvitto - ${$time(e.date).format('yyyy-MM-dd')}.pdf`
                })
            }
        ], { width: -30 });

        transaction_def.controller = row => {
            if(row.item.is_failed)
                row.classList.add('tl-failed-row');

            return undefined;
        }

        const transaction_source = new YArrayDataSource<EHUserTransaction>();

        cUserGetInfo(user.id, 'membership').then(info => {
            transactions = info.transactions.filter(t => t.kind != 'redeem');
            transaction_source.data = transactions.sort((a, b) => $time.compare($time(b.date), $time(a.date)));
            action_source.data = info.actions.slice().reverse();
        });

        this.ui_add($el.FORM('ui-section w-block l-form', [
            $el.H3('heading', 'Subscriptions'),
            new UITable(subs_def, subs_source, { sort: [0, -1], classes: 'b b-content' }),
            $el.H3('heading', 'Active discounts / addons'),
            new UITable(addon_def, addon_source, { classes: 'b b-content' }),
            $el.H3('heading', 'Actions'),
            new UITable(action_def, action_source, { classes: 'b b-content' }),
            $el.H3('heading', 'Transactions'),
            new UITable(transaction_def, transaction_source, { classes: 'b b-content' })
        ]));
    }
}

class UserBookingsTab extends UIView {
    constructor(user: CUserAdminView) {
        super('div', 'tl-admin-page');

        const refreshTable = () => { source.reload() };

        const def = new UITableDefinition<CBookingAdminView>();
        def.addValue(b => b.number, { title: 'Number', width: -80 });
        def.addValue(b => b.event_name, { title: 'Event', width: 150 });
        def.addValue(b => $time(b.session_time), $fmt.DATE(), { title: 'Session', width: -100 });
        def.addValue(b => $time(b.created_at), $fmt.DATE(), { title: 'Booked at', width: -100 });
        def.addValue(b => b.quantity, $fmt.NUMBER, { title: 'Amount', width: -80 });
        def.addValue(b => b.status, { title: 'State', width: -100 });
        def.addButtons([
            { icon: UIIcon.XMARK, tooltip: 'Reject', action: booking => cBookingPartReject(booking.part_id).then(refreshTable), when: i => i.status == 'standby_reserved' || i.status == 'reserved' },
            { icon: UIIcon.CHECK, tooltip: 'Confirm', action: booking => cBookingPartConfirm(booking.part_id).then(refreshTable), when: i => i.status == 'standby_reserved' || i.status == 'reserved' },
            {
                icon: UIIcon.TRASH_CAN, tooltip: 'Force unbook', action: booking => showWarning({ title: 'Removing booking', text: 'Are you sure?' }).then(() => {
                    cBookingForceUnbook(booking.id).then(refreshTable);
                }), when: i => i.status == 'confirmed' && $time(i.session_time).is_future
            },
            {
                icon: UIIcon.CIRCLE_PLUS, tooltip: 'Add free tickets', action: booking => {
                    new AddFreeTicketsDialog(booking).present().then(refreshTable);
                }, when: i => i.status == 'confirmed' && $time(i.session_time).is_future
            },
            {
                icon: UIIcon.CIRCLE_MINUS, tooltip: 'Remove 1', action: booking => cBookingPartChangeQuantity(booking.part_id, booking.quantity - 1).then(refreshTable),
                when: i => i.status == 'standby' && $time(i.session_time).is_future && i.quantity > 1
            },
            { icon: UIIcon.ANGLES_UP, tooltip: 'Prioritize', action: booking => cBookingPartPrioritize(booking.part_id).then(refreshTable), when: i => i.status == 'standby' && $time(i.session_time).is_future && !i.is_test && !i.is_priority },
            { icon: UIIcon.WAND_MAGIC_SPARKLES, tooltip: 'Reserve', action: booking => cBookingPartStandbyReserve(booking.part_id).then(refreshTable), when: i => i.status == 'standby' && $time(i.session_time).is_future && i.is_test },
            { icon: UIIcon.TRIANGLE_EXCLAMATION, tooltip: 'Mark as no-show', action: booking => cBookingNoShow(booking.id).then(refreshTable), when: i => xavis_experiments && i.status == 'confirmed' && $time(i.session_time).is_past },
        ], { width: -62 });

        def.controller = row => {
            const model = row.item;

            if(model.is_late_cancel)
                row.classList.add('tl-late-row');
            if(model.status == 'cancelled' || (model.status == 'standby' && $time(model.session_time).is_past))
                row.classList.add('tl-canceled-row');
            else if(model.status == 'standby')
                row.classList.add('tl-standby-row');
            else if(model.status == 'confirmed' && $time(model.session_time).is_future)
                row.classList.add('tl-active-row');
            else if(model.status == 'standby_reserved' || model.status == 'reserved')
                row.classList.add('tl-reserved-row');

            return undefined;
        }

        const source = new YArrayDataSource<CBookingAdminView>();

        const section = $el.FORM('ui-section w-block l-form', [
            new UITable(def, source)
        ]);

        const button = new UIButton({
            icon: UIIcon.PLUS, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => cSessionsGetUpcoming().then(sessions => {
                new AddBookingDialog(user, sessions).present();
            })
        });
        addTooltip('Add booking', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, button]);

        cBookingsAdminViewByUser(user.id).then(bookings => {
            source.data = bookings.reverse();
        });
    }
}

class UserMessagesTab extends UIView {
    constructor(user: CUserAdminView) {
        super('div', 'tl-admin-page');

        const def = new UITableDefinition<MDelivery>();

        def.addValue(m => $time(m.sent_at), $fmt.DATE('yyyy-MM-dd HH:mm'), { title: 'Time', width: -150 });
        def.addValue(m => m.message.channel, { title: 'Channel', width: -100 });
        def.addValue(m => m.message.code, { title: 'Code', width: 150 });
        def.addValue(m => m.message.open_at ? 'open' : m.status, { title: 'State', width: -100 });

        def.addButtons([
            { icon: UIIcon.ARROW_UP_RIGHT_FROM_SQUARE, when: r => !!r.message.body_hash, tooltip: 'See message', link: p => '/docs/m/messages/body/' + p.message.body_hash }
        ], { width: -32 });

        const source = new YArrayDataSource<MDelivery>();

        const section = $el.FORM('ui-section w-form l-form', [
            new UITable(def, source)
        ]);

        cUserGetInfo(user.id, 'messages').then(emails => {
            source.data = emails.filter(e => (e.message.code != 'invite.invite' && e.message.code != 'invite.reminder')).sort((a, b) => b.message.created_at.localeCompare(a.message.created_at));
        });

        const button = new UIButton({
            icon: UIIcon.PAPER_PLANE, kind: 'k-square', classes: 'm-round ui-action-button', on_click: () => {
                new AdminSendMessageDialog(user).present();
            }
        });
        addTooltip('Send email', button, { offset: 8, hover: button, position: UIRelPos.Above });

        this.ui_add([section, button]);
    }
}

class UserPermissionsTab extends UIView {
    constructor(user: CUserAdminView) {
        super('div', 'tl-admin-page');

        const def = new UITableDefinition<LPermissionSpec>();
        let permissions = Object.keys(user.permissions);

        const checkboxDecorator = (td: HTMLTableCellElement, item: LPermissionSpec, kind: 'view' | 'manage') => {
            if(!item[kind]) return;
            const checkbox = new UITickCheckbox({ classes: 'u-inline' });
            const key = item.key + '.' + kind as LPermission
            checkbox.value = permissions.includes(key);
            if(canManagePermissions()) {
                $on(checkbox, 'value', v => {
                    if(v) {
                        if(!permissions.includes(key))
                            permissions.push(key);
                    } else {
                        permissions = permissions.filter(p => p != key);
                    }

                    cUserUpdatePermissions(user.id, permissions).then(() => {
                        user.permissions = Object.fromEntries(permissions.map(p => [p, true]));
                    })
                })
            } else {
                checkbox.disabled = true;
            }

            td.classList.add('m-no-padding');
            td.ui_add(checkbox);
        }

        def.addValue(p => p.name, { title: 'Permission', width: -180 });
        def.addCustom({ title: 'View', width: -100, classes: 'm-center', decorator: (td, row) => checkboxDecorator(td, row.item, 'view') });
        def.addCustom({ title: 'Manage', width: -100, classes: 'm-center', decorator: (td, row) => checkboxDecorator(td, row.item, 'manage') });

        this.ui_add($el.FORM('ui-section w-form l-form', [
            new UITable(def, new YArrayDataSource(xavis_experiments ? l_permission_specs : l_permission_specs.filter(s => !s.experiment)))
        ]));
    }
}

export class AdminUserScreen extends UIScreen {
    constructor(private _user_id?: YId) {
        super();
        this.title = _user_id ? 'User' : 'New user';
    }

    protected override fillContent(content: UIViewSpec): void {
        content.classList.add('tl-tabs');

        if(this._user_id) {
            cUserAdminViewGet(this._user_id).then(user => {
                this.title = cUserFullName(user);
                const tab_spec = [
                    { title: 'Info', view: () => new UserInfoTab(user) },
                    { title: 'Subscriptions', view: () => new UserSubscriptionTab(user) },
                    { title: 'Bookings', view: () => new UserBookingsTab(user) },
                    { title: 'Roles', view: () => new UserRolesTab(user) },
                    { title: 'Messages', view: () => new UserMessagesTab(user) }
                ];

                if(canViewPermissions())
                    tab_spec.push({ title: 'Permissions', view: () => new UserPermissionsTab(user) })

                content.ui_add(new UITabs(tab_spec));
            })
        } else {
            content.ui_add(new UITabs([
                { title: 'Info', view: () => new UserInfoTab }
            ]));
        }
    }
}
